import { EndSessionMessage } from "../../messages/session/SessionMessages";
import { Module } from "../module";
import { RequestSTTMessage, StartSTTRecordMessage, STTMessage, TTSMessage, STTStartedMessage, STTEndedMessage, StopSTTRecordMessage } from "../../messages/stt/STTMessages";
import { ModuleId } from "flavr-common";
// The maximum returned alternatives for each result.
const MAX_ALTERNATIVES = 5;
/**
 * Wraps the browser Web Speech APIs so speech-to-text requests and text-to-speech playback can be exposed through the module bus.
 *
 * Messages:
 * - `RequestSTTMessage`, `StartSTTRecordMessage` and `StopSTTRecordMessage` - Start, stop and coordinate speech recognition requests, including ask/answer based STT workflows.
 * - `TTSMessage` - Speaks the provided text through the browser speech synthesis engine.
 * - `EndSessionMessage` - Stops active recognition and clears the current model association when a session ends.
 */
export class WebSpeechSTT extends Module {
    /**
     * Registers the subscriptions for STT ask requests, start/stop recording, text-to-speech playback and
     * session-end cleanup.
     */
    constructor() {
        super(ModuleId.WEBSPEECH_STT);
        this.isListening = false;
        this.isStopping = false;
        this.language = "de-DE";
        this.askIdLists = [];
        this.currentInterimResult = [];
        this.onstart = () => {
            this.connection.send(new STTStartedMessage());
        };
        this.startRecord = (msg) => {
            if (this.currentModelUID === undefined) {
                this.currentModelUID = msg.uid;
            }
            this.start();
        };
        this.stopRecord = () => {
            this.forceStop(false);
        };
        this.handleEndSessionMessage = () => {
            this.currentModelUID = undefined;
            this.forceStop(false);
        };
        this.onResult = (event /* SpeechRecognitionEvent */) => {
            const finalResults = [];
            for (let i = 0, len = event.results.length; i < len; ++i) {
                const result = event.results[i];
                if (result.isFinal) {
                    this.appendAlternativeResults(result, finalResults);
                }
                else {
                    this.currentInterimResult = [];
                    this.appendAlternativeResults(result, this.currentInterimResult);
                }
            }
            if (finalResults.length > 0) {
                this.connection.send(new STTMessage(finalResults, this.currentModelUID));
                if (this.askIdLists.length) {
                    this.sendAskRequests(new STTMessage(finalResults, this.currentModelUID));
                }
            }
            this.checkToStop();
        };
        this.onNoMatch = () => {
            this.sendAskRequests();
            this.checkToStop();
            console.warn("[stt] Couldn't recognize speech command");
        };
        this.onEnd = () => {
            this.sendAskRequests();
            this.forceStop();
            this.connection.send(new STTEndedMessage());
        };
        this.onError = (event) => {
            this.forceStop();
            if (event.error === "no-speech") {
                console.warn("[stt] No speech");
            }
            else if (event.error === "audio-capture") {
                console.warn("[stt] No microphone");
            }
            else {
                console.warn(`[stt] Error occurred in recognition: ${event.error}`);
            }
        };
        /**
         * Speaks the given text aloud through the browser's speech synthesis engine, using the module's configured
         * language.
         */
        this.speak = (msg) => {
            if (typeof SpeechSynthesisUtterance === "undefined") {
                console.warn("[stt] SpeechSynthesisUtterance isn't supported");
                return;
            }
            const utterance = new SpeechSynthesisUtterance();
            /*
            The text attribute allows you to set the text that you wish to be spoken.
             */
            utterance.text = msg.text;
            /*
            The lang attribute gives you the ability to specify the language of the text.
             */
            utterance.lang = this.language;
            /*
            The volume property allows you to adjust the volume of the speech. A value between 0 and 1 should be specified here. Default is 1.
             */
            utterance.volume = 1;
            /*
            The rate attribute defines the speed at which the text should be spoken. This should be a between 0 and 10. Default is 1
             */
            utterance.rate = 1;
            /*
            The pitch attribute controls how high or low the text is spoken. This should be a value between 0 and 2. Default is 1.
             */
            utterance.pitch = 1;
            window.speechSynthesis.speak(utterance);
        };
        this.connection.subscribeForAskRequest(RequestSTTMessage, (msg, askId) => {
            this.askIdLists.push(askId);
            if (this.recognition && this.isListening) {
                if (!msg.waitForFinalResult) {
                    this.forceStop(false);
                }
            }
            else {
                this.sendAskRequests();
            }
        });
        this.connection.subscribeByType(StartSTTRecordMessage, this.startRecord);
        this.connection.subscribeByType(StopSTTRecordMessage, this.stopRecord);
        this.connection.subscribeByType(TTSMessage, this.speak);
        this.connection.subscribeByType(EndSessionMessage, this.handleEndSessionMessage);
    }
    /**
     * Creates and configures the browser's `webkitSpeechRecognition` instance (continuous, interim results,
     * configured language and max alternatives) and wires up all its event callbacks. Does nothing if the
     * browser does not support speech recognition.
     */
    init() {
        if (!("webkitSpeechRecognition" in window)) {
            return;
        }
        // webkitSpeechRecognition is not part for the typings so we have to use this workaround
        this.recognition = new window.webkitSpeechRecognition();
        if (!this.recognition) {
            return;
        }
        this.recognition.continuous = true;
        this.recognition.interimResults = true;
        this.recognition.lang = this.language;
        this.recognition.maxAlternatives = MAX_ALTERNATIVES;
        this.recognition.onstart = this.onstart;
        this.recognition.onresult = this.onResult;
        this.recognition.onsoundend = () => {
            this.checkToStop();
        };
        this.recognition.onspeechend = () => {
            this.forceStop();
        };
        this.recognition.onnomatch = this.onNoMatch;
        this.recognition.onend = this.onEnd;
        this.recognition.onerror = this.onError;
    }
    appendAlternativeResults(result, dest) {
        // we have max 5 alternatives
        for (let j = 0, len2 = result.length; j < len2; ++j) {
            const alternative = result[j];
            dest.push({
                text: alternative.transcript,
                confidence: alternative.confidence
            });
        }
    }
    /**
     * Answers every pending `RequestSTTMessage` ask with the given message (or an empty response if none is
     * given), then clears the pending ask id list.
     */
    sendAskRequests(message) {
        if (this.askIdLists.length === 0)
            return;
        for (const aksId of this.askIdLists) {
            if (message) {
                this.connection.send(message, aksId);
            }
            else {
                this.connection.sendMultiple([], aksId);
            }
        }
        this.askIdLists = [];
    }
    start(continuous = true) {
        if (this.recognition && !this.isListening) {
            this.recognition.continuous = continuous;
            this.isListening = true;
            this.recognition.start();
        }
        this.isStopping = false;
    }
    /**
     * Requests that recognition be stopped; the actual stop is deferred until the current sound/speech segment
     * ends and `checkToStop` runs.
     */
    stop() {
        this.isStopping = true;
    }
    /**
     * Performs the deferred stop requested via `stop`, if any is still pending.
     */
    checkToStop() {
        if (this.isStopping) {
            this.forceStop();
        }
    }
    /**
     * Immediately stops the active recognition session, optionally answers pending ask requests, and notifies
     * the associated model that recognition has stopped.
     */
    forceStop(sendAsk = true) {
        if (this.recognition && this.isListening) {
            this.isListening = false;
            this.recognition.stop();
            if (sendAsk)
                this.sendAskRequests();
            this.currentModelUID = undefined;
        }
    }
}
